import {
  Box,
  EditIcon,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Typography
} from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { getVesselDetailsByFilters } from '@api';
import { useSegment } from '@hooks';
import VesselOverview from '@components/Sections/ShipAnalytics/VesselProfile/SubSections/VesselOverviewSection/VesselOverview/VesselOverview';
import { formatNumberReadable, formatToDecimalPlaces } from '@helpers';
import SelectDepartureAndArrivalPort, { PortSelectionType } from '../SelectDepartureAndArrivalPort';
import { useTimezone } from '@hooks/useTimezone';
import { useQuery } from '@tanstack/react-query';

const AdjacentPortsDirection = {
  NEXT: 'next',
  PREVIOUS: 'previous'
};
const getAdjacentPorts = (
  portCalls,
  currentPort,
  count,
  direction = AdjacentPortsDirection.NEXT
) => {
  const currentIndex = portCalls.findIndex(
    (port) => port?.portId === currentPort?.portId && port?.arrivalDate === currentPort?.arrivalDate
  );
  const isNext = direction === AdjacentPortsDirection.NEXT;
  const start = isNext ? currentIndex + 1 : Math.max(0, currentIndex - count);
  const end = isNext ? currentIndex + 1 + count : currentIndex;

  return portCalls.slice(start, end);
};

const VoyageSummaryOverview = ({
  isLoading,
  summaryOverview,
  voyageTransitDetails,
  latestPortCall,
  isOngoingVoyage,
  voyagePortCalls = [],
  onUpdateDepartureAndArrivalPort
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isRoRo } = useSegment();
  const [updatedPortSelectionType, setUpdatedPortSelectionType] = useState();
  const { getTimeZoneDisplay } = useTimezone();

  const currentDeparturePort = voyagePortCalls.find(
    (port) => port.portId === voyageTransitDetails?.fromPortId
  );
  const currentArrivalPort = voyagePortCalls.find(
    (port) => port.portId === voyageTransitDetails?.toPortId
  );

  const nexFiveDeparturePortCalls =
    isOngoingVoyage || !voyagePortCalls.length
      ? []
      : getAdjacentPorts(voyagePortCalls, currentDeparturePort, 5);
  const previousFiveArrivalPortCalls =
    isOngoingVoyage || !voyagePortCalls.length
      ? []
      : getAdjacentPorts(voyagePortCalls, currentArrivalPort, 5, AdjacentPortsDirection.PREVIOUS);

  const renderEditPortAction = (portSelectionType, isDisabled) => {
    if (!voyagePortCalls?.length || isOngoingVoyage) return null;
    return (
      <IconButton
        disabled={isDisabled}
        onClick={() => {
          setUpdatedPortSelectionType(portSelectionType);
        }}
        sx={{
          m: 0
        }}>
        <EditIcon fontSize="small" color={isDisabled ? 'gray' : 'white'} />
      </IconButton>
    );
  };

  const arrivalPortData = {
    label: 'Arrival Port:',
    value: isOngoingVoyage ? '-' : summaryOverview?.arrivalPort,
    id: voyageTransitDetails?.toPortId,
    renderAction: () =>
      renderEditPortAction(PortSelectionType.ARRIVAL, previousFiveArrivalPortCalls.length < 2)
  };

  const arrivalTimeData = {
    label: 'Arrival Time:',
    value: getTimeZoneDisplay(summaryOverview?.arrivalTime)
  };

  const intermediatePortData = {
    label: isOngoingVoyage ? 'No. of Ports called:' : 'No. of Intermediate Port Calls:',
    value: summaryOverview?.numPortsVisited
  };

  const transitTimeData = {
    label: 'Transit Time:',
    value: formatToDecimalPlaces(voyageTransitDetails?.transitDays, 2) + ' Days'
  };

  const totalDistanceData = {
    label: 'Total Distance:',
    value:
      formatNumberReadable(formatToDecimalPlaces(voyageTransitDetails?.totalDistance, 0)) + ' NM'
  };

  const overviewData = [
    { label: 'Vessel Name:', value: summaryOverview?.vesselName, id: summaryOverview?.imo },
    {
      label: isOngoingVoyage ? 'Latest Port call:' : 'Departure Port:',
      value: isOngoingVoyage ? latestPortCall?.portName : summaryOverview?.departurePort,
      id: voyageTransitDetails?.fromPortId,
      renderAction: () =>
        renderEditPortAction(PortSelectionType.DEPARTURE, nexFiveDeparturePortCalls.length < 2)
    },
    {
      label: 'Departure Time:',
      value: isOngoingVoyage
        ? getTimeZoneDisplay(latestPortCall?.endDate)
        : getTimeZoneDisplay(summaryOverview?.departureTime)
    },
    arrivalPortData,
    arrivalTimeData,
    intermediatePortData,
    transitTimeData,
    totalDistanceData
  ];

  const vesselDetails = useQuery({
    enabled: !!summaryOverview?.imo,
    queryKey: ['vesselDetails', { imos: [summaryOverview?.imo] }],
    queryFn: ({ signal, queryKey }) => {
      return getVesselDetailsByFilters(queryKey[1], signal)
        .then((res) => res.vesselInformation[0])
        .catch(() => ({}));
    }
  });

  return (
    <>
      <Paper
        sx={{
          p: isOngoingVoyage ? 0 : 2,
          backgroundColor: isOngoingVoyage ? 'transparent' : undefined,
          height: '100%'
        }}>
        <Stack>
          <Typography variant={'h6'} align="left" sx={{ p: 2 }}>
            Overview
          </Typography>
          {isLoading ? (
            <Skeleton variant={'rectangular'} height={'25vh'}></Skeleton>
          ) : (
            <ul id="voyage-summary-overview">
              {overviewData
                .filter((d) =>
                  isOngoingVoyage
                    ? d.label !== arrivalTimeData.label &&
                      d.label !== arrivalPortData.label &&
                      d.label !== transitTimeData.label &&
                      d.label !== totalDistanceData.label
                    : true
                )
                .map(({ label, value, id, renderAction }) => {
                  const isVesselName = label === 'Vessel Name:';
                  return (
                    <li key={label} style={{ marginBottom: '8px' }}>
                      <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}>
                        <Typography variant={'body1'}>{label}</Typography>
                        <Box display={'flex'} alignItems={'center'}>
                          {(isVesselName || !id) && (
                            <Typography
                              mr={2}
                              variant={'body1'}
                              onMouseEnter={(event) => {
                                if (isVesselName) {
                                  setAnchorEl(event.currentTarget);
                                }
                              }}
                              onMouseLeave={() => {
                                if (isVesselName) {
                                  setAnchorEl(null);
                                }
                              }}
                              sx={{ cursor: isVesselName ? 'pointer' : 'default' }}>
                              {value}
                            </Typography>
                          )}

                          {id &&
                            !isVesselName &&
                            !(isOngoingVoyage && label === arrivalPortData.label) && (
                              <Box>
                                <ProfileLink
                                  uriParams={'section=1'}
                                  profile={label === 'Vessel Name:' ? 'vessel' : 'port'}
                                  id={id}
                                  name={
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                      <Typography>{value}</Typography>
                                    </Stack>
                                  }
                                />
                              </Box>
                            )}
                          {renderAction && renderAction()}
                        </Box>
                        {isVesselName && (
                          <Popover
                            id="voyage-info-popover"
                            sx={{
                              pointerEvents: 'none'
                            }}
                            open={Boolean(anchorEl)}
                            anchorEl={anchorEl}
                            onClose={() => setAnchorEl(null)}
                            disableRestoreFocus
                            PaperProps={{
                              onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                              onMouseLeave: () => setAnchorEl(null),
                              style: { pointerEvents: 'auto' }
                            }}>
                            <VesselOverview
                              vesselsDataByFilters={vesselDetails.data}
                              loading={vesselDetails.isFetching}
                              isRoRo={isRoRo}
                              title="Voyage Specification"
                              width="700px"
                              showTitle={false}
                            />
                          </Popover>
                        )}
                      </Stack>
                    </li>
                  );
                })}
            </ul>
          )}
        </Stack>
      </Paper>
      {!isOngoingVoyage &&
        voyageTransitDetails?.fromPortId &&
        voyageTransitDetails?.toPortId &&
        updatedPortSelectionType && (
          <SelectDepartureAndArrivalPort
            open
            onClose={() => {
              setUpdatedPortSelectionType(undefined);
            }}
            onSave={(selectedDepartureAndArrivalPort) => {
              onUpdateDepartureAndArrivalPort?.(selectedDepartureAndArrivalPort);
              setUpdatedPortSelectionType(undefined);
            }}
            arrivalPorts={previousFiveArrivalPortCalls}
            departurePorts={nexFiveDeparturePortCalls}
            currentDeparturePort={currentDeparturePort}
            currentArrivalPort={currentArrivalPort}
            portSelectionType={updatedPortSelectionType}
          />
        )}
    </>
  );
};

export default VoyageSummaryOverview;

VoyageSummaryOverview.propTypes = {
  summaryOverview: PropTypes.object,
  latestPortCall: PropTypes.object,
  isLoading: PropTypes.bool,
  voyageTransitDetails: PropTypes.object,
  isOngoingVoyage: PropTypes.bool,
  voyagePortCalls: PropTypes.array,
  onUpdateDepartureAndArrivalPort: PropTypes.func
};

VoyageSummaryOverview.defaultProps = {
  summaryOverview: null,
  isLoading: false,
  voyageTransitDetails: null
};
